import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import { accountFeatureKey } from '@core/account-types';
import type { PricingFeatureSubscription } from '@core/billing-types';
import { BillingPlanTier } from '@core/billing-types';
import { getProductCodenameFromSubscription } from '@core/billing-util';
import { COMPANY_ID } from '@core/common-legacy';
import type { Company } from '@core/company-types';
import { createActionsWithReducer, createFeatureSelector, ngrxEvent } from '@core/ngrx-legacy';

import { CurrentUserSelectors } from '../store/current-user/current-user.store';

export interface AccountState {
  currentCompanyId: number | null;
}

export const AccountConfigToken = new InjectionToken('AccountConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<AccountState> => ({
    initialState: {
      currentCompanyId: inject(COMPANY_ID),
    },
  }),
});

const initialState: AccountState = {
  currentCompanyId: null,
};

const baseSelector = createFeatureSelector<AccountState>(accountFeatureKey);
const { actions, reducer } = createActionsWithReducer('Account', initialState, {
  setCurrentCompany: (state, payload: { id: Company['id'] }) => ({ currentCompanyId: payload.id }),
  changeCompany: ngrxEvent<Company>(),
  deleteRequest: ngrxEvent<string>(),
  redirectToBlockedAccount: ngrxEvent<void>(),
  signOut: (state, payload?: { afterSignOutUrl?: string }) => state,
});
export { actions as AccountActions, reducer as accountReducer };

const currentCompanySelector = createSelector(
  baseSelector,
  CurrentUserSelectors.data,
  function currentCompany(accountState, currentUser) {
    return currentUser?.companies
      ? currentUser.companies.find(({ id }: { id: number }) => id === accountState.currentCompanyId)
      : null;
  },
);

export const isEEOEnabledSelector = createSelector(
  currentCompanySelector,
  company => <boolean>company?.settings.eeoEnabled,
);

export const isSSOEnabledSelector = createSelector(
  currentCompanySelector,
  company => company?.ssoActive,
);

export const areRequisitionsEnabledSelector = createSelector(
  currentCompanySelector,
  company => !!company?.settings.requisitionsEnabled,
);

const productCodenameSelector = createSelector(
  currentCompanySelector,
  company =>
    getProductCodenameFromSubscription(<PricingFeatureSubscription>company?.subscription) || null,
);

export const selectSettings = () =>
  createSelector(currentCompanySelector, company => (company ? company.settings : null));

export const selectAbilities = () =>
  createSelector(currentCompanySelector, company => (company ? company.abilities : []));

const isMultilangEnabled = createSelector(
  currentCompanySelector,
  company => company?.settings.multilangEnabled || false,
);

const currentCompanyName = createSelector(currentCompanySelector, company => company?.name);

const isOnLegacyPlanSelector = createSelector(productCodenameSelector, productCodename => {
  if (!productCodename) return false;

  const legacyPlans = [BillingPlanTier.Lead, BillingPlanTier.Scale, BillingPlanTier.Launch];
  return legacyPlans.some(plan => productCodename.toLowerCase().includes(plan.toLowerCase()));
});

export const AccountSelectors = {
  currentCompany: currentCompanySelector,
  productCodename: productCodenameSelector,
  isEEOEnabled: isEEOEnabledSelector,
  isSSOEnabled: isSSOEnabledSelector,
  isMultilangEnabled,
  areRequisitionsEnabled: areRequisitionsEnabledSelector,
  isOnLegacyPlan: isOnLegacyPlanSelector,
  selectIsGdprSettingEnabled: createSelector(
    selectSettings(),
    settings => settings && settings['gdprEnabled'],
  ),
  currentCompanyWithUser: createSelector(
    currentCompanySelector,
    CurrentUserSelectors.data,
    function currentCompanyWithUser(currentCompany, currentUser) {
      return currentUser
        ? {
            currentCompany: currentCompany || null,
            currentUser,
          }
        : null;
    },
  ),
  currentCompanyName,
};
